import { ArpMediaType, getUrlForMedia, hasComposition, KeyAndScale } from '@arpeggi/shared'
import { clone, destroy, unprotect } from 'mobx-state-tree'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useInRouterContext, useLocation } from 'react-router-dom'
import { IArpMedia } from '../../../features/arp-registry/ArpMedia'
import ArpRegistry from '../../../features/arp-registry/ArpRegistry'
import {
  getSampleIndexFromOriginToken,
  getSongIndexFromOriginToken,
  getStemIndexFromOriginToken
} from '../../api/CoreAPI'
import { getSharedDraft } from '../../api/v2/ArpDraftsAPI'
import { useAuth } from '../../hooks/useAuth'
import { useMintStatuses } from '../../queries/mintStatuses'
import { useUser } from '../../queries/users'
import ProfileRegistry from '../../stores/registry/ProfileRegistry'
import { fetchArpWaveform } from '../../stores/registry/RegistryUtils'
import { useTreeState } from '../../stores/Root'
import SharedAudioContext from '../../stores/SharedAudioContext'
import { deserializeSongFromStorage, ISongStore, SongStore } from '../../stores/song/SongStore'
import { IStudioStore, StudioStore } from '../../stores/StudioStore'
import { defaultProfPic, useStatus } from '../core/Utils'

type PlayerTypes = 'song' | 'sample' | 'stem' | 'none' | 'draft'

export const usePlayerParamsResolver = () => {
  const isRouter = useInRouterContext()
  // eslint-disable-next-line react-hooks/rules-of-hooks
  const { search } = isRouter ? useLocation() : window.location

  const { type, originToken, draftCode } = useMemo(() => {
    const params = new URLSearchParams(search)
    const type = (params.get('type') || 'none') as PlayerTypes
    const originToken = params.get('token') || 'none'
    const draftCode = params.get('draft') || 'none'

    return { type, originToken, draftCode }
  }, [search])

  return { originToken: Number(originToken), type, draftCode }
}

const preloadImage = (url: string): Promise<string> =>
  new Promise((resolve, reject) => {
    try {
      if (!url) throw new Error('no image found')
      const image = new Image()
      image.src = url
      image.onerror = () => {
        resolve(defaultProfPic())
      }
      image.onload = () => {
        resolve(image.src)
      }
    } catch (err) {
      resolve(defaultProfPic())
    }
  })

export interface ArtistData {
  url: string
  name: string
  avatar: string
}

export const prepareArtistDataFromEth = async (eth: string): Promise<ArtistData> => {
  const profileData = await ProfileRegistry.getByIndexAsync(eth)
  if (!profileData) throw new Error('no profile found')
  const avatar =
    profileData && profileData ? await preloadImage(profileData.profile_picture) : defaultProfPic()
  return { avatar, name: profileData.display_name || 'Unknown', url: /profile/ + eth }
}

export const useSamplePlayer = (originToken: number, autoPlay?: boolean, token?: number) => {
  const { globalPlayer } = useTreeState()
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading')
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | undefined>()
  const [audio, setAudio] = useState<HTMLAudioElement>(new Audio())
  const [title, setTitle] = useState<string>('')
  const [artist, setArtist] = useState<ArtistData>()
  const [instrument, setInstrument] = useState('')
  const [genre, setGenre] = useState('')
  const [tempo, setTempo] = useState<number>()
  const [key, setKey] = useState<string>('')
  const [timesUsed, setTimesUsed] = useState<number | 'loading'>('loading')
  const [isSyncedWithGlobalPlayer, setIsSyncedWithGlobalPlayer] = useState<boolean>(false)
  const [autoplayOnRerender, setAutoplayOnRerender] = useState<boolean>(false)
  const [hasAutoplayed, setHasAutoplayed] = useState<boolean>(false)
  const previousOriginToken = useRef<number>()
  const previousToken = useRef<number>()

  useEffect(() => {
    audio.pause()
    if (previousOriginToken.current !== originToken || previousToken.current !== token) {
      setStatus('loading')
    }
    previousToken.current = token
    previousOriginToken.current = originToken
    const load = async () => {
      // fetch sample meta
      let sampleIndex: number
      if (token !== null && token !== undefined) sampleIndex = token
      else sampleIndex = await getSampleIndexFromOriginToken(originToken)
      const data = await ArpRegistry.getByIdAsync(sampleIndex)

      setGenre(
        (data.metadata.genres && data.metadata.genres.length > 0 && genre) ||
          data.metadata.genres.reduce((p, c) => (p.length > 0 ? ', ' : '' + c), '')
      )
      setTimesUsed(data.remixCount)

      setInstrument(data.category || 'Unknown')
      if (data.metadata.bpm) setTempo(data.metadata.bpm)
      setKey(KeyAndScale.from(data.metadata.key).displayName())

      // fetch audioBuffer
      const buffer = await data.fetchAudioBuffer()
      setAudioBuffer(buffer)

      // fetch artist data
      setArtist(await prepareArtistDataFromEth(data.user.walletAddress))

      // set title from meta
      setTitle(data.metadata.title)

      const globallySynced = globalPlayer.arpMediaIndex === sampleIndex
      setIsSyncedWithGlobalPlayer(globallySynced)

      // if the selected player is already synced with the global player, we're good to go
      let newAudio: HTMLAudioElement = null
      if (globallySynced) {
        setAudio(globalPlayer.getAudio())
        if (globalPlayer.state === 'ready') {
          setStatus('ready')
        } else {
          globalPlayer
            .getAudio()
            .addEventListener('loadedmetadata', () => setStatus('ready'), { once: true })
        }
      } else {
        // if the selected player isn't synced with the global player, we still want the player
        // to render, and when it's actually played we will sync it with the global player
        newAudio = new Audio()
        newAudio.volume = 0

        newAudio.src = getUrlForMedia(data.asInternalArpMedia())
        newAudio.addEventListener('loadedmetadata', () => {
          setStatus('ready')
        })
        setAudio(newAudio)

        newAudio.addEventListener(
          'play',
          async () => {
            if (sampleIndex === globalPlayer.arpMediaIndex) return

            newAudio.pause()
            setAutoplayOnRerender(true)
            await globalPlayer.setArpMediaIndex(sampleIndex, true)
            setAudio(globalPlayer.getAudio())
            globalPlayer.play()
          },
          { once: true }
        )
      }
    }

    load().catch((e) => {
      console.error(e)
      setStatus('error')
    })
  }, [originToken, token, globalPlayer.arpMediaIndex])

  useEffect(() => {
    if (status === 'ready' && ((autoPlay && !hasAutoplayed) || autoplayOnRerender)) {
      isSyncedWithGlobalPlayer ? globalPlayer.play() : audio.play()
      setAutoplayOnRerender(false)
      setHasAutoplayed(true)
    }
    return () => !isSyncedWithGlobalPlayer && audio.pause()
  }, [status, autoPlay, isSyncedWithGlobalPlayer])

  useEffect(() => {
    setHasAutoplayed(false)
  }, [token, originToken, autoPlay])

  return { status, audioBuffer, title, artist, audio, instrument, genre, timesUsed, tempo, key }
}

export const useUploadSoundPlayerData = ({
  audioFile,
  dataUri,
  genres
}: {
  audioFile: File
  dataUri: string
  genres: string[]
}) => {
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading')
  const [audioBuffer, setAudioBuffer] = useState<AudioBuffer | undefined>()
  const [audio, setAudio] = useState<HTMLAudioElement | undefined>()
  const [resolvedGenres, setResolvedGenres] = useState('')
  const [artist, setArtist] = useState<ArtistData>()
  const { user } = useAuth()

  const fetchSampleAudioBuffer = async () => {
    const audioFileArrayBuffer = await audioFile.arrayBuffer()

    return await SharedAudioContext.decodeAudioData(audioFileArrayBuffer)
  }

  useEffect(() => {
    setStatus('loading')

    if (audio) audio.pause()

    const load = async () => {
      setResolvedGenres(
        genres.reduce((previous, current) => (previous.length > 0 ? ', ' : '' + current), '')
      )

      const artist = await prepareArtistDataFromEth(user.wallet_address)
      setArtist(artist)

      const buffer = await fetchSampleAudioBuffer()
      setAudioBuffer(buffer)

      // initialize html audio element
      const audio = new Audio(encodeURI(dataUri))

      audio.oncanplay = () => {
        setAudio(audio)
        setStatus('ready')
      }
    }

    load().catch((e) => {
      console.error(e)
      setStatus('error')
    })
  }, [genres])

  return {
    status,
    audioBuffer,
    artist,
    audio,
    genre: resolvedGenres
  }
}

const formatMilliseconds = (time: number) => String(Math.floor((time % 1) * 100)).padStart(2, '0')
const formatSeconds = (time: number) => String(Math.floor(time % 60)).padStart(2, '0')
const formatMinutes = (time: number) => String(Math.floor(time / 60)).padStart(2, '0')

/**
 * @param seconds number value in seconds
 * @returns `string` formatted like `mm:ss`
 */
export const formatTime = (time: number, includeMS?: boolean) => {
  const minutes = formatMinutes(time)
  const seconds = formatSeconds(time)
  let formatted = `${minutes}:${seconds}`
  if (includeMS) formatted += `:${formatMilliseconds(time)}`
  return formatted
}

export interface StudioSongPlayerProps {
  status: 'loading' | 'error' | 'ready'
  title: string
  artist: ArtistData
  player: IStudioStore
  song: ISongStore
  bpm: number
  stem?: boolean
}

export const useDraftPlayer = (draftCode: string): StudioSongPlayerProps => {
  const player = useNewStudioStore()
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading')
  const [artist, setArtist] = useState<ArtistData>()

  useEffect(() => {
    const setup = async () => {
      await player.initialize(false)
      const draft = await getSharedDraft(draftCode)
      setArtist(await prepareArtistDataFromEth(draft.user.walletAddress))
      await player.loadSongFromDraft(draft.arp)
      setStatus('ready')
    }

    setup().catch((err) => {
      console.error(err)
      setStatus('error')
    })
  }, [draftCode])

  return {
    status,
    player,
    title: player.song.name,
    artist,
    bpm: player.song.tempo,
    song: player.song
  }
}

export const usePreviewSongPlayer = (): StudioSongPlayerProps => {
  const { player, studio } = useTreeState()
  const [status, setStatus] = useState<'loading' | 'error' | 'ready'>('loading')
  const { user } = useAuth()
  const [artist, setArtist] = useState<ArtistData>()

  useEffect(() => {
    const setup = async () => {
      if (!user || !user.wallet_address) {
        throw new Error('No user found')
      }
      try {
        await player.initialize(false)
        player.reset()
        const songClone = clone(studio.song)
        songClone.minimizeForStorage()
        setArtist(await prepareArtistDataFromEth(user.wallet_address))
        await player.setSong(songClone)
        setStatus('ready')
      } catch (error) {
        console.error(error)
        setStatus('error')
      }
    }
    setup().catch((err) => {
      console.warn(err)
      setStatus('error')
    })
  }, [])

  useEffect(() => {
    if (player.song) player.song.setTokenColor(studio.song.tokenColor)
  }, [studio.song.tokenColor])

  return {
    status,
    artist,
    title: studio.song.name,
    player,
    bpm: player.song.tempo,
    song: player.song
  }
}

export const useAltSongPreview = (index: number, autoPlay = true): StudioSongPlayerProps => {
  const { alternatePlayer: player } = useTreeState()
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>('loading')
  const [artist, setArtist] = useState<ArtistData>()
  const [title, setTitle] = useState('')

  useEffect(() => {
    if (index < 0) return
    setStatus('loading')
    const setup = async () => {
      await player.initialize(false)
      player.reset()
      const song = await ArpRegistry.getByIdAsync(index)
      if (!song) throw new Error('[useAltSongPreview] failed to fetch song')
      setTitle(song.metadata.title)
      setArtist(await prepareArtistDataFromEth(song.user.walletAddress))
      await player.loadSongFromArpMedia(song)
      setStatus('ready')
      if (autoPlay) player.play()
    }
    setup().catch((err) => {
      console.warn(err)
      setStatus('error')
    })
    return () => {
      player.reset()
    }
  }, [index])
  return { status, artist, player, title, bpm: player.song.tempo, song: player.song }
}

export const useTimelineOverlay = (audio: HTMLAudioElement) => {
  const overlay = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const updateStyles = () => {
      if (!overlay.current) return
      overlay.current.style.transform = `translateX(${(audio.currentTime / audio.duration) * 100}%)`
      if (!audio.paused) requestAnimationFrame(updateStyles)
    }

    const startAnimation = () => {
      requestAnimationFrame(updateStyles)
    }

    const updateOverlay = () => {
      if (!audio.paused) return
      requestAnimationFrame(updateStyles)
    }

    if (!audio.paused) startAnimation()

    audio.addEventListener('play', startAnimation)
    audio.addEventListener('timeupdate', updateOverlay)
    return () => {
      audio.removeEventListener('play', startAnimation)
      audio.removeEventListener('timeupdate', updateOverlay)
    }
  }, [audio])

  return overlay
}

interface PlayerData {
  song: ISongStore
  title: string
  artist: ArtistData
  player: HTMLAudioElement | IStudioStore
  buffer?: AudioBuffer
  bpm: number
}

const isValidURL = (str: string) => {
  const pattern = new RegExp(
    '^(https?:\\/\\/)?' + // protocol
      '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
      '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
      '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
      '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
      '(\\#[-a-z\\d_]*)?$',
    'i'
  ) // fragment locator
  return !!pattern.test(str)
}

export const useNewStudioStore = () => {
  const studioStore = useMemo(() => {
    const studio = StudioStore.create()
    unprotect(studio)
    return studio
  }, [])
  useEffect(() => {
    return () => destroy(studioStore)
  }, [studioStore])
  return studioStore
}

/** Fetches Arp media for a song with either the given originToken or arpId. */
const fetchSongArpMedia = async (originToken: number, arpId: number): Promise<IArpMedia> => {
  try {
    let index
    if (arpId !== null && arpId !== undefined) index = arpId
    else index = await getSongIndexFromOriginToken(originToken)
    if (index === null) return null
    return await ArpRegistry.getByIdAsync(index)
  } catch {
    return null
  }
}

/** Fetches Arp media for a stem with either the given originToken or arpId. */
const fetchStemArpMedia = async (originToken: number, arpId: number): Promise<IArpMedia> => {
  try {
    let index
    if (arpId !== null && arpId !== undefined) index = arpId
    else index = await getStemIndexFromOriginToken(originToken)
    if (index === null) return null
    return await ArpRegistry.getByIdAsync(index)
  } catch {
    return null
  }
}

export const useStemPlayer = (originToken: number, arpId: number = null, autoPlay = false) => {
  return useSongOrStemPlayer(false, originToken, arpId, autoPlay)
}

export const useSongPlayer = (originToken: number, arpId: number = null, autoPlay = false) => {
  return useSongOrStemPlayer(true, originToken, arpId, autoPlay)
}

/** Player for both songs and stems. Plays Mp3/Wav file if available, falls back to rendering full composition if not. */
const useSongOrStemPlayer = (
  isSong: boolean,
  originToken: number,
  arpId: number = null,
  autoPlay = false
) => {
  const { globalPlayer } = useTreeState()
  const [status, setStatus] = useStatus()
  const [data, setData] = useState<PlayerData>()
  const [mediaHasComposition, setMediaHasComposition] = useState(true)
  const [mediaNotFound, setMediaNotFound] = useState(false)
  const [type, setType] = useState<'wav' | 'raw'>('raw')
  const player = useNewStudioStore()
  const [audio, setAudio] = useState<HTMLAudioElement>(new Audio())
  const [isSyncedWithGlobalPlayer, setIsSyncedWithGlobalPlayer] = useState<boolean>(false)
  const [autoplayOnRerender, setAutoplayOnRerender] = useState<boolean>(false)
  const [hasAutoplayed, setHasAutoplayed] = useState<boolean>(false)
  const arpMediaType = isSong ? ArpMediaType.SONG : ArpMediaType.STEM
  const previousOriginToken = useRef<number>()
  const previousArpId = useRef<number>()

  useEffect(() => {
    if (previousOriginToken.current !== originToken || previousArpId.current !== arpId) {
      setStatus('loading')
    }
    previousArpId.current = arpId
    previousOriginToken.current = originToken
    audio.pause()
    const setup = async () => {
      if (player.isPlaying) player.reset()

      let arpMedia: IArpMedia = null
      if (isSong) {
        arpMedia = await fetchSongArpMedia(originToken, arpId)
      } else {
        arpMedia = await fetchStemArpMedia(originToken, arpId)
      }

      if (!arpMedia) {
        setMediaNotFound(true)
        return
      }
      // If arpMedia doesn't have a composition, we fall back to use sample player.
      if (!hasComposition(arpMedia.asInternalArpMedia())) {
        setMediaHasComposition(false)
        return
      }

      const title = arpMedia.metadata.title || 'Unknown'
      const artist = await prepareArtistDataFromEth(arpMedia.user.walletAddress)
      const bpm = arpMedia.metadata.bpm

      // We always need the composition, for displaying to the user.
      let songComposition: ISongStore
      if (isSong) {
        songComposition = deserializeSongFromStorage(arpMedia.metadata.arpeggiComposition)
      } else {
        // If we have a stem, we need to create a song that wraps it.
        songComposition = SongStore.create()
        unprotect(songComposition)
        const stem = await ArpRegistry.getByIdAsync(arpMedia.id)
        songComposition.addStemFromRemix(stem, false)
        songComposition.tempo = arpMedia.metadata.bpm
        songComposition.tokenColor = stem.tokenColor

        // TODO pressing play seems to be playing the stem back too slowly.
      }

      const dataUri = getUrlForMedia(arpMedia.asInternalArpMedia())

      if (dataUri && dataUri.length > 0) {
        // Initialize HTML player with wav or Mp3 file.
        console.log('[valid url]', dataUri)

        const bytesArray = await fetchArpWaveform(arpMedia.asInternalArpMedia())
        const decodedBuffer = await SharedAudioContext.decodeAudioData(bytesArray)

        const globallySynced = globalPlayer.arpMediaIndex === arpMedia.id
        setIsSyncedWithGlobalPlayer(globallySynced)

        // if the selected player is already synced with the global player, we're good to go
        let newAudio: HTMLAudioElement = null
        if (globallySynced) {
          setAudio(globalPlayer.getAudio())
          if (globalPlayer.state === 'ready') {
            setType('wav')
            setStatus('ready')
          } else {
            globalPlayer.getAudio().addEventListener(
              'loadedmetadata',
              async () => {
                setType('wav')
                setStatus('ready')
              },
              { once: true }
            )
          }
        } else {
          // if the selected player isn't synced with the global player, we still want the player
          // to render, and when it's actually played we will sync it with the global player
          newAudio = new Audio()
          newAudio.volume = 0

          newAudio.src = dataUri
          newAudio.addEventListener(
            'loadedmetadata',
            async () => {
              setType('wav')
              setStatus('ready')
            },
            { once: true }
          )
          setAudio(newAudio)

          newAudio.addEventListener(
            'play',
            async () => {
              if (arpMedia.id === globalPlayer.arpMediaIndex) return

              newAudio.pause()
              setAutoplayOnRerender(true)
              await globalPlayer.setArpMediaIndex(arpMedia.id, true)
              setAudio(globalPlayer.getAudio())
              globalPlayer.play()
            },
            { once: true }
          )
        }

        setData({
          song: songComposition,
          title,
          artist,
          bpm,
          player: globallySynced ? globalPlayer.getAudio() : newAudio,
          buffer: decodedBuffer
        })
      } else {
        // Fall back to just using the composition
        await player.initialize(false)
        player.reset()
        setData({ song: songComposition, title, artist, bpm, player })
        await player.setSong(songComposition)
        setType('raw')
        setStatus('ready')
      }
    }

    setup().catch((err) => {
      console.warn('[player]', err)
      setStatus('error')
    })
  }, [originToken, arpId, globalPlayer.arpMediaIndex])

  useEffect(() => {
    if (status === 'ready' && ((autoPlay && !hasAutoplayed) || autoplayOnRerender)) {
      type === 'wav' && isSyncedWithGlobalPlayer ? globalPlayer.play() : audio.play()
      type === 'raw' && player.play()
      setAutoplayOnRerender(false)
      setHasAutoplayed(true)
    }
    return () => {
      try {
        player.reset()
      } catch (err) {
        console.warn(err)
      }
      !isSyncedWithGlobalPlayer && audio.pause()
    }
  }, [status, autoPlay, isSyncedWithGlobalPlayer, autoplayOnRerender])

  useEffect(() => {
    setHasAutoplayed(false)
  }, [arpId, originToken, autoPlay])

  return { status, data, type, mediaHasComposition, mediaNotFound }
}

export const useSongFromMintStatusPlayer = (originTokenId: number) => {
  const { player } = useTreeState()
  const [playerStatus, setPlayerStatus] = useState<'loading' | 'error' | 'ready'>('loading')
  const mintStatusesQuery = useMintStatuses({ params: { tokenId: originTokenId } })
  const userQuery = useUser({
    walletAddressOrId: mintStatusesQuery?.data?.pages[0].data?.[0]?.user.walletAddress,
    options: { enabled: !!mintStatusesQuery?.data?.pages[0].data?.[0]?.user.walletAddress }
  })

  useEffect(() => {
    if (!userQuery.isLoading && !mintStatusesQuery.isLoading) {
      setPlayerStatus('ready')
    }
  }, [mintStatusesQuery?.isLoading, userQuery?.isLoading])

  useEffect(() => {
    const setup = async () => {
      const deserialized = deserializeSongFromStorage(
        mintStatusesQuery.data?.[0]?.arpeggiComposition
      )
      await player.initialize(false)
      await player.setSong(deserialized)
    }

    if (mintStatusesQuery.data) {
      setup().catch((err) => console.warn('[useSongFromMintStatusPlayer] error', err))
    }
  }, [mintStatusesQuery.data?.[0]?.arpeggiComposition])

  const user = userQuery.data
  const mintStatus = mintStatusesQuery?.data?.[0]

  return { player, playerStatus, mintStatus, user }
}
